import { Component, OnInit } from '@angular/core';
import { EMPTY, expand, finalize, forkJoin } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ChartDataset } from 'chart.js';
import moment from 'moment';
import { orderBy } from 'lodash';

import {
  ChartDatasetFilter,
  ChartDatasetMax,
  ChartLabel,
} from 'src/app/core/entities/chart-dataset';
import { WalkingAid, walkingAidMapping } from 'src/app/core/enums/walking-aid';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';

@Component({
  selector: 'app-tests',
  templateUrl: './tests.component.html',
  styleUrls: ['./tests.component.css'],
})
export class TestsComponent implements OnInit {
  private _features: any = {};
  public evaluations = {
    0: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_PERIMETER,
      title: $localize`:@@patient_evaluationOverviewTests_evaluationPerimeterTitle:Walking perimeter`,
      features: [
        {
          featureId: FeatureService.FEATURE_PERIMETER_WD,
          title: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWDTitle:Perimeter`,
          label: $localize`:@@patient_evaluationPerimeter_featurePerimeterWDLabel:Perimeter (in m)`,
          min: 0,
          max: 1000,
          isMaxLimited: true,
          chartDatasetOption: {
            type: 'line',
            color: '#0e5cdd',
            nbSteps: 10,
            tooltip: {
              label: $localize`:@@patient_evaluationPerimeter_featurePerimeterWDTooltipLabel:Perimeter`,
              unit: 'm',
            },
            pointStyle: 'circle',
          },
          progression: 0,
        },
      ],
    },
    1: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_6MWT,
      title: $localize`:@@patient_evaluationOverviewTests_evaluation6MWTTitle:Endurance`,
      features: [
        {
          featureId: FeatureService.FEATURE_6MWT_6MIN_WS_MEAN,
          title: $localize`:@@patient_evaluationOverviewTests_feature6MWT6MinWSMeanTitle:Average speed`,
          label: $localize`:@@patient_evaluationOverviewTests_feature6MWT6MinWSMeanLabel:Average speed in 6 min (in km/h)`,
          min: 0,
          max: 5,
          isMaxLimited: true,
          chartDatasetOption: {
            type: 'line',
            color: '#0e5cdd',
            nbSteps: 6,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewTests_feature6MWT6MinWSMeanTooltipLabel:Average speed`,
              unit: 'km/h',
            },
            nbDecimalDigits: 1,
            pointStyle: 'circle',
          },
          progression: 0,
        },
      ],
    },
    2: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_VISION,
      title: $localize`:@@patient_evaluationOverviewTests_evaluationVisionTitle:Vision`,
      features: [
        {
          featureId: FeatureService.FEATURE_VISION_BIN_MONOYER,
          title: $localize`:@@patient_evaluationOverviewTests_featureVisionBinMonoyerTitle:Acuity monoyer`,
          label: $localize`:@@patient_evaluationOverviewTests_featureVisionBinMonoyerLabel:Acuity monoyer (binocular)`,
          min: 0,
          max: 10,
          chartDatasetOption: {
            type: 'line',
            color: '#0e5cdd',
            useTickModel: true,
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewTests_featureVisionBinMonoyerTooltipLabel:Binocular / Acuity monoyer`,
              unit: '',
              pattern: /(.*)/,
              replace: '$1/10',
            },
            nbDecimalDigits: 0,
            nbSteps: 10,
            pointStyle: 'circle',
          },
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewTests_evaluationVisionHorizontalLimitLineYLabel:Maximum acuity`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 10,
          },
        },
      ],
    },
    3: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_DEXTERITY,
      title: $localize`:@@patient_evaluationOverviewTests_evaluationDexterityTitle:Dexterity`,
      features: [
        {
          featureId: FeatureService.FEATURE_DRAW_RIGHT_PWD_MEAN,
          title: $localize`:@@patient_evaluationOverviewTests_featureDrawRightPWDMeanTitle:Right hand precision`,
          label: [
            $localize`:@@patient_evaluationOverviewTests_featureDrawRightPWDMeanLabelPart1:Right hand precision`,
            $localize`:@@patient_evaluationOverviewTests_featureDrawRightPWDMeanLabelPart2:(deviation from the plot in mm)`,
          ],
          min: -6,
          max: 0,
          isMinLimited: true,
          chartDatasetOption: {
            type: 'line',
            color: '#0dc49c',
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewTests_featureDrawRightPWDMeanTooltipLabel:Right hand precision`,
              unit: 'mm',
            },
            pointStyle: 'circle',
            axis: {
              id: 'drawRightPwdMean',
              type: 'linear',
              position: 'right',
              beginAtZero: true,
            },
          },
          progression: 0,
        },
        {
          featureId: FeatureService.FEATURE_DRAW_LEFT_PWD_MEAN,
          title: $localize`:@@patient_evaluationOverviewTests_featureDrawLeftPWDMeanLabel:Left hand precision`,
          label: [
            $localize`:@@patient_evaluationOverviewTests_featureDrawLeftPWDMeanLabelPart1:Left hand precision`,
            $localize`:@@patient_evaluationOverviewTests_featureDrawLeftPWDMeanLabelPart2:(deviation from the plot in mm)`,
          ],
          min: -6,
          max: 0,
          isMinLimited: true,
          chartDatasetOption: {
            type: 'line',
            color: '#0e5cdd',
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewTests_featureDrawLeftPWDMeanTooltipLabel:Left hand precision`,
              unit: 'mm',
            },
            pointStyle: 'circle',
          },
          progression: 0,
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewTests_evaluationDexterityHorizontalLimitLineYLabel:Maximum precision`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 0,
            drawRightPwdMean: 0,
          },
        },
      ],
    },
    4: <ChartEvaluation>{
      evaluationCode: EvaluationService.EVALUATION_COGNITION,
      title: $localize`:@@patient_evaluationOverviewTests_evaluationCognitionLabel:Cognition`,
      features: [
        {
          featureId: FeatureService.FEATURE_CPS_STD_AR_RT_MEAN,
          title: $localize`:@@patient_evaluationOverviewTests_featureCPSSTDARRTMeanLabel:Speed of response`,
          label: [
            $localize`:@@patient_evaluationOverviewTests_featureCPSSTDARRTMeanLabelPart1:Speed of response`,
            $localize`:@@patient_evaluationOverviewTests_featureCPSSTDARRTMeanLabelPart2:(Latence of responses in ms)`,
          ],
          min: -5000,
          max: 0,
          isMinLimited: true,
          chartDatasetOption: {
            type: 'line',
            color: '#0e5cdd',
            tooltip: {
              label: $localize`:@@patient_evaluationOverviewTests_featureCPSSTDARRTMeanTooltipLabel:Speed of response`,
              unit: 'ms',
            },
            pointStyle: 'circle',
          },
          progression: 0,
        },
      ],
      horizontalLimitLines: [
        {
          label: $localize`:@@patient_evaluationOverviewTests_evaluationCognitionHorizontalLimitLineYLabel:Maximum speed`,
          color: '#3d3d40',
          assignToAxisIds: {
            y: 0,
          },
        },
      ],
    },
  };
  public datasetMax = {
    perimeter: <ChartDatasetMax>{
      featureId: FeatureService.FEATURE_PERIMETER_COMP,
      assignToFeatures: [FeatureService.FEATURE_PERIMETER_WD],
    },
  };
  public datasetFilters = {
    perimeter: <ChartDatasetFilter>{
      title: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWATitle:Filtering data by type of aid used`,
      legend: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWALegend:realised evaluation with walking aid`,
      featureId: FeatureService.FEATURE_PERIMETER_WA,
      pointStyle: 'triangle',
      options: [
        {
          title: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWANoHelpTitle:Show performances with no help`,
          label: walkingAidMapping[WalkingAid.NO_HELP],
          value: WalkingAid.NO_HELP,
          order: 3,
          hideOnTooltip: true,
        },
        {
          title: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWAUnilateralHelpTitle:Show performances with unilateral help`,
          label: walkingAidMapping[WalkingAid.UNILATERAL_HELP],
          value: WalkingAid.UNILATERAL_HELP,
          order: 0,
          pointStyle: 'triangle',
        },
        {
          title: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWABilateralHelpTitle:Show performances with bilateral help`,
          label: walkingAidMapping[WalkingAid.BILATERAL_HELP],
          value: WalkingAid.BILATERAL_HELP,
          order: 1,
          pointStyle: 'triangle',
        },
        {
          title: $localize`:@@patient_evaluationOverviewTests_featurePerimeterWAAccompanyingPersonTitle:Show performances with accompanying person`,
          label: walkingAidMapping[WalkingAid.ACCOMPANYING_PERSON],
          value: WalkingAid.ACCOMPANYING_PERSON,
          order: 2,
          pointStyle: 'triangle',
        },
      ],
    },
    endurance: <ChartDatasetFilter>{
      title: $localize`:@@patient_evaluationOverviewTests_feature6MWTWATitle:Filtering data by type of aid used`,
      legend: $localize`:@@patient_evaluationOverviewTests_feature6MWTWALegend:realised evaluation with walkign aid`,
      featureId: FeatureService.FEATURE_6MWT_WA,
      pointStyle: 'triangle',
      options: [
        {
          title: $localize`:@@patient_evaluationOverviewTests_feature6MWTWANoHelpTitle:Show performances with no help`,
          label: walkingAidMapping[WalkingAid.NO_HELP],
          value: WalkingAid.NO_HELP,
          order: 3,
          hideOnTooltip: true,
        },
        {
          title: $localize`:@@patient_evaluationOverviewTests_feature6MWTWAUnilateralHelpTitle:Show performances with unilateral help`,
          label: walkingAidMapping[WalkingAid.UNILATERAL_HELP],
          value: WalkingAid.UNILATERAL_HELP,
          order: 0,
          pointStyle: 'triangle',
        },
        {
          title: $localize`:@@patient_evaluationOverviewTests_feature6MWTWABilateralHelpTitle:Show performances with bilateral help`,
          label: walkingAidMapping[WalkingAid.BILATERAL_HELP],
          value: WalkingAid.BILATERAL_HELP,
          order: 1,
          pointStyle: 'triangle',
        },
        {
          title: $localize`:@@patient_evaluationOverviewTests_feature6MWTWAAccompanyingPersonTitle:Show performances with accompanying person`,
          label: walkingAidMapping[WalkingAid.ACCOMPANYING_PERSON],
          value: WalkingAid.ACCOMPANYING_PERSON,
          order: 2,
          pointStyle: 'triangle',
        },
      ],
    },
  };
  public datasets: any = {};
  public xLabels: any = {};
  public loading = true;

  constructor(
    private featureService: FeatureService,
    private chartService: ChartService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.setOverviewCharts();
    this.evaluations = orderBy(this.evaluations, ['order']);
  }

  /**
   * Sets overview charts
   */
  setOverviewCharts(): void {
    let sources$ = {};

    for (const [key, evaluation] of Object.entries(this.evaluations)) {
      this.xLabels[key] = [] as ChartLabel[];
      this.datasets[key] = [] as ChartDataset[];
      this._features[key] = [] as Feature[];

      let params = {
        page: 1,
        startDate: moment()
          .subtract(12, 'months')
          .utc(true)
          .startOf('month')
          .toISOString(),
        endDate: moment().utc(true).endOf('month').toISOString(),
        patientUuid: this.route.snapshot.params['uuid'],
        featureIds: evaluation.features
          .map((item) => item.featureId)
          .concat(
            this.datasetFilters[key] !== undefined
              ? this.datasetFilters[key].featureId
              : []
          )
          .concat(
            this.datasetMax[key] !== undefined
              ? this.datasetMax[key].featureId
              : []
          ),
      };

      sources$[key] = this.featureService.getFeatures(params).pipe(
        expand((res) => {
          params.page++;
          this._features[key] = this._features[key].concat(res);

          return res.length < 50
            ? EMPTY
            : this.featureService.getFeatures(params);
        })
      );
    }

    forkJoin(sources$)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          for (const [key, value] of Object.entries(this._features)) {
            let chartDataPoints = {};

            this.evaluations[key].features.forEach((feature) => {
              let groupedFeatures = this.featureService.groupFeatures(
                value as Feature[],
                feature.featureId
              );
              chartDataPoints[feature.featureId] =
                this.chartService.setChartDataPoints(groupedFeatures);
            });

            if (Object.values(chartDataPoints).length > 0) {
              // Set labels
              const maxDataPoints = Object.values(chartDataPoints).reduce(
                (r: any, s: any) => (r.length > s.length ? r : s),
                0
              );
              this.xLabels[key] = this.chartService.setXLabels(
                maxDataPoints as ChartDataPoint[]
              );

              // Set filter features
              let chartDataFilterPoints = [];
              if (this.datasetFilters[key] !== undefined) {
                let groupedFilterFeatures = this.featureService.groupFeatures(
                  this._features[key],
                  this.datasetFilters[key].featureId
                );

                chartDataFilterPoints =
                  this.chartService.setChartDataFilterPoints(
                    groupedFilterFeatures
                  );
              }

              // Add max features to datasets
              let chartDataMaxPoints = [];
              if (this.datasetMax[key] !== undefined) {
                let groupedMaxFeatures = this.featureService.groupFeatures(
                  this._features[key],
                  this.datasetMax[key].featureId
                );
                chartDataMaxPoints =
                  this.chartService.setChartDataPoints(groupedMaxFeatures);
              }

              // Set datasets
              this.evaluations[key].features.forEach((feature, j) => {
                const dataset = this.chartService.setDataset(
                  feature,
                  chartDataPoints[feature.featureId],
                  feature.chartDatasetOption,
                  chartDataFilterPoints,
                  this.xLabels[key],
                  feature.min ??
                    (chartDataPoints[feature.featureId].length
                      ? chartDataPoints[feature.featureId][0].min
                      : null),
                  feature.max ??
                    (chartDataPoints[feature.featureId].length
                      ? chartDataPoints[feature.featureId][0].max
                      : null),
                  feature.isMaxLimited,
                  feature.isMinLimited,
                  chartDataMaxPoints,
                  this.datasetMax[key] !== undefined
                    ? this.datasetMax[key].assignToFeatures.includes(
                        feature.featureId
                      )
                    : false
                );
                this.datasets[key].push(dataset);

                feature['progression'] = this.chartService.getProgression(
                  chartDataPoints[feature.featureId],
                  feature.chartDatasetOption.tooltip !== undefined
                    ? feature.chartDatasetOption.tooltip.unit === '%'
                    : false
                );
              });
            }
          }
        },
      });
  }
}
